import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Plus, ArrowRightLeft, Heart } from 'lucide-react';
import { CardInstance } from '../../types/collection.ts';
import { getCardInstances, toggleForTrade } from '../../lib/collectionService.ts';
import { addToSeekingList, removeFromSeekingList } from '../../lib/exchangeService.ts';

interface TradeListManagerProps {
  userId: string;
  mode: 'forTrade' | 'seeking';
  seekingList?: string[];
  onUpdate?: () => void;
}

export default function TradeListManager({
  userId,
  mode,
  seekingList = [],
  onUpdate
}: TradeListManagerProps) {
  const [cards, setCards] = useState<CardInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  const loadCards = useCallback(async () => {
    setLoading(true);
    try {
      const instances = await getCardInstances(userId);
      setCards(instances);
    } catch (error) {
      console.error('Failed to load cards:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const handleToggleForTrade = async (instanceId: string) => {
    setUpdating(instanceId);
    try {
      await toggleForTrade(userId, instanceId);
      await loadCards();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to toggle trade status:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleSeeking = async (cardId: string) => {
    setUpdating(cardId);
    try {
      if (seekingList.includes(cardId)) {
        await removeFromSeekingList(userId, cardId);
      } else {
        await addToSeekingList(userId, cardId);
      }
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update seeking list:', error);
    } finally {
      setUpdating(null);
    }
  };

  const filteredCards = cards.filter(card =>
    card.cardId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const forTradeCards = filteredCards.filter(c => c.forTrade);
  const availableCards = filteredCards.filter(c => !c.forTrade);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 bg-gray-800 rounded-lg" />
        ))}
      </div>
    );
  }

  if (mode === 'forTrade') {
    return (
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search your cards..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* For Trade Section */}
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-green-400" />
            For Trade ({forTradeCards.length})
          </h3>
          {forTradeCards.length === 0 ? (
            <p className="text-gray-500 text-sm py-4 text-center">
              No cards marked for trade yet
            </p>
          ) : (
            <div className="space-y-2">
              {forTradeCards.map(card => (
                <div
                  key={card.id}
                  className="flex items-center justify-between p-3 bg-green-900/20 border border-green-800/30 rounded-lg"
                >
                  <div>
                    <p className="text-sm font-medium text-white">{card.cardId}</p>
                    <p className="text-xs text-gray-400">
                      Qty: {card.quantity} • {card.condition}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleForTrade(card.id!)}
                    disabled={updating === card.id}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    {updating === card.id ? (
                      <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Available Cards Section */}
        <div>
          <h3 className="text-sm font-medium text-gray-300 mb-2">
            Available to Trade ({availableCards.length})
          </h3>
          {availableCards.length === 0 ? (
            <p className="text-gray-500 text-sm py-4 text-center">
              {searchQuery ? 'No cards match your search' : 'All cards are marked for trade'}
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableCards.slice(0, 20).map(card => (
                <div
                  key={card.id}
                  className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                >
                  <div>
                    <p className="text-sm font-medium text-white">{card.cardId}</p>
                    <p className="text-xs text-gray-400">
                      Qty: {card.quantity} • {card.condition}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleForTrade(card.id!)}
                    disabled={updating === card.id}
                    className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors"
                  >
                    {updating === card.id ? (
                      <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
              {availableCards.length > 20 && (
                <p className="text-xs text-gray-500 text-center py-2">
                  Showing 20 of {availableCards.length} cards. Use search to find specific cards.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Seeking mode
  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search for cards to add..."
          className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Currently Seeking */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
          <Heart className="w-4 h-4 text-pink-400" />
          Currently Seeking ({seekingList.length})
        </h3>
        {seekingList.length === 0 ? (
          <p className="text-gray-500 text-sm py-4 text-center">
            No cards in your seeking list
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {seekingList.map(cardId => (
              <div
                key={cardId}
                className="flex items-center gap-2 px-3 py-1.5 bg-pink-500/20 border border-pink-500/30 rounded-full"
              >
                <span className="text-sm text-pink-400">{cardId}</span>
                <button
                  onClick={() => handleToggleSeeking(cardId)}
                  disabled={updating === cardId}
                  className="text-pink-400 hover:text-pink-300"
                >
                  {updating === cardId ? (
                    <div className="w-3 h-3 border border-pink-400/30 border-t-pink-400 rounded-full animate-spin" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Add cards to your seeking list from the card detail pages or by searching above.
      </p>
    </div>
  );
}
